import { Scrollbars } from 'react-custom-scrollbars-2';
import {
  Avatar,
  Box,
  Button,
  Link,
  Tooltip,
  useTheme,
  CSSObject,
  Stack,
  Typography
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import SidebarMenu from './SidebarMenu';
import { useAppSelector, useAppDispatch } from 'src/app/hooks';
import { toogleSidebar } from 'src/features/sidebarToggleSlice';
import LogoNoLimitWhite from 'src/components/LogoNoLimitWhite';
import { USERS_SIGN_IN, SETTINGS } from 'src/route';
import { useLocation, useNavigate } from 'react-router';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import MiniSidebarMenu from './MiniSidebarMenu';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlackScreenComponent from '../BlackScreenComponent';
import { FC, useState } from 'react';
import ProjectListDialog from 'src/components/DialogProjectList';
import { useTranslation } from 'react-i18next';

interface ToogleSidebar {
  open: boolean;
  isOnTour?: boolean;
}

interface Item {
  name: string;
}

const openedMixin = (theme): CSSObject => ({
  width: '180px',
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden',
  border: 'none'
});

const closedMixin = (theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `74px`,
  border: 'none'
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  width: '180px',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme)
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme)
  })
}));

const SidebarWrapper = styled(Box)<ToogleSidebar>(
  ({ theme, open, isOnTour }) => `
    min-width: ${open ? '180px' : 0};
    width: inherit;
    color: ${theme.sidebar.textColor};
    background: ${theme.sidebar.background};
    box-shadow: ${theme.sidebar.boxShadow};
    height: 100%;
    position: relative;
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        position: fixed;
        ${!isOnTour && 'z-index: 10'};
        position: relative;
    }
    &:hover {
      & #custom-icon {
        opacity: 1
      }
    }
`
);

const TopSection = styled(Box)<ToogleSidebar>(
  ({ theme, open }) => `
    display: flex;
    height: 88px;
    align-items: center;
    justify-content: ${open ? 'start' : 'center'};
    margin: 0 ${open ? `${theme.spacing(1)} ${theme.spacing(3)}` : 0};
    padding-left: 4px;
    padding-right: 4px
`
);

const CustomAvatar = styled(Avatar)(
  ({ theme }) => `
    cursor: pointer;
`
);

const SidebarContainer = styled(Box)<ToogleSidebar>(
  ({ theme, open }) => `
    width: ${open ? '180px' : '74px'};
    transition: width 0.1s linear;
`
);

interface IMainContentProps {
  open: boolean;
  toggleSidebar: () => void;
}

export const MainContent: FC<IMainContentProps> = ({ open, toggleSidebar }) => {
  const theme = useTheme();
  const customerType = useAppSelector(
    (state) => state.users.project.customerType
  );
  const projectName = useAppSelector(
    (state) => state.users.project.displayName
  );

  const project = useAppSelector((state) => state.users.project);

  const projectList = useAppSelector(
    (state) => state.users.userProfile.data.projectList
  );

  const navigate = useNavigate();
  // const posthog = usePostHog();
  const { t } = useTranslation();
  //
  const [openProjectList, setOpenProjectList] = useState(false);

  const handleClickOpen = () => {
    setOpenProjectList(true);
  };
  const handleClose = () => {
    setOpenProjectList(false);
  };

  const linkToHome = () => {
    navigate('/');
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('project');
    // posthog.reset();
    window.location.href = USERS_SIGN_IN;
  };

  const { pathname } = useLocation();
  const mainLevelMenu = pathname.split('/')[1];
  const isMatchCurrentPath = (url: string) => mainLevelMenu === url;

  const { isTourButtonOpen, isOnTour } = useAppSelector(
    (state) => state.storeProductTour
  );

  const isB2C = customerType === 'b2c';

  return (
    <>
      {(isTourButtonOpen || isOnTour) && <BlackScreenComponent />}
      <SidebarWrapper open={open} isOnTour={isOnTour}>
        <Scrollbars autoHide>
          <TopSection open={open} isOnTour={isOnTour}>
            <Tooltip title={open ? 'Close sidebar' : 'Open sidebar'}>
              <CustomAvatar
                variant="rounded"
                id="custom-icon"
                onClick={toggleSidebar}
                sx={{
                  backgroundColor: theme.sidebar.background,
                  justifyContent: open ? 'start' : 'center'
                }}
              >
                <DoubleArrowIcon
                  sx={
                    open && {
                      transform: 'scaleX(-1)'
                    }
                  }
                  fontSize="small"
                />
              </CustomAvatar>
            </Tooltip>
            {open && (
              <Link
                onClick={linkToHome}
                underline="none"
                sx={{ cursor: 'pointer' }}
              >
                <LogoNoLimitWhite />
              </Link>
            )}
          </TopSection>
          {open ? (
            <Stack gap={2} px={1} pb={2}>
              <Typography
                variant="body1"
                sx={{
                  textTransform: 'uppercase',
                  color: '#F5F5F5',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '130%'
                }}
              >
                {t('project')}
              </Typography>
              <Typography
                variant={'h4'}
                overflow="hidden"
                textOverflow="ellipsis"
                fontSize="14px"
                color="#fff"
              >
                {projectName}
              </Typography>
              {projectList.length > 1 && (
                <Button
                  variant="outlined"
                  sx={{
                    background: '#E9F7FE',
                    border: '1px solid rgba(5, 28, 38, 0.08)',
                    fontWeight: 600,
                    color: '#285771'
                  }}
                  size="small"
                  onClick={handleClickOpen}
                  endIcon={<AutorenewIcon sx={{ color: '#285771' }} />}
                >
                  Switch
                </Button>
              )}
            </Stack>
          ) : null}
          {open ? <SidebarMenu /> : <MiniSidebarMenu />}
          {!open && (
            <Button
              onClick={(e) => navigate(SETTINGS)}
              sx={{
                display: 'flex',
                minWidth: '0',
                width: '70%',
                marginInline: 'auto',
                borderRadius: '0.5em',
                ...(isMatchCurrentPath(SETTINGS.slice(1)) && {
                  background: '#064E66'
                })
              }}
            >
              <SettingsIcon sx={{ color: 'white' }} />
            </Button>
          )}
          <Stack direction="column" justifyContent="end" paddingX={1} gap={1}>
            {isB2C && open && (
              <Button
                variant="contained"
                size="small"
                sx={{ fontWeight: 600 }}
                onClick={() => navigate(`${SETTINGS}?parent_tab=billing`)}
              >
                Manage Plan
              </Button>
            )}
            <Button
              sx={
                open
                  ? {
                    color: theme.sidebar.menuItemBgActive,
                    paddingLeft: '0px',
                    justifyContent: 'left'
                  }
                  : {
                    width: '100%',
                    color: theme.sidebar.menuItemBgActive
                  }
              }
              color="inherit"
              onClick={handleSignOut}
            >
              {open ? (
                <Stack direction="row" gap={0.5}>
                  <LogoutIcon /> {t('signout')}
                </Stack>
              ) : (
                <LogoutIcon />
              )}
            </Button>
          </Stack>
        </Scrollbars>
      </SidebarWrapper>
      <ProjectListDialog
        open={openProjectList}
        onClose={handleClose}
        project={project}
        projectList={projectList}
      />
    </>
  );
};

function Sidebar() {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  // const posthog = usePostHog();
  const sidebarToggle = useAppSelector((state) => state.sidebarToggle.toggle);
  const toggleSidebar = () => {
    dispatch(toogleSidebar(!sidebarToggle));
  };

  return (
    <SidebarContainer open={sidebarToggle}>
      <Drawer
        anchor="left"
        open={sidebarToggle}
        onClose={toggleSidebar}
        variant="permanent"
        elevation={9}
        sx={{
          maxWidth: theme.sidebar.width
        }}
      >
        <MainContent open={sidebarToggle} toggleSidebar={toggleSidebar} />
      </Drawer>
    </SidebarContainer>
  );
}

export default Sidebar;
